import {
  importLocalDataKek,
  localAccountKeyError,
} from '@streamlain/view-config/local-data-seal';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

import { raiseAccountMirrors } from '../impls/idb/account-mirrors';
import type { LocalAuthBinding } from '../storage/local-data-access';

export interface LocalAccountKekVault {
  read(
    accountId: string,
    keyId: string
  ): Promise<{ status: 'ready' | 'unavailable'; kek: Uint8Array | null }>;
  write(
    accountId: string,
    keyId: string,
    deviceId: string,
    kek: Uint8Array
  ): Promise<'ready' | 'unavailable'>;
  prune(accountId: string, keepKeyId: string | null): Promise<void>;
}

export type LocalAccountKekCustody =
  | { kind: 'record' }
  | { kind: 'os-vault'; vault: LocalAccountKekVault };

export interface LocalAccountKeyRecord {
  accountId: string;
  deviceId: string;
  authEpoch: number;
  logoutPending: boolean;
  kek: CryptoKey | null;
  envelope: string | null;
  keyId: string | null;
}

interface AccountKeySchema extends DBSchema {
  account: { key: string; value: LocalAccountKeyRecord };
}

export interface LocalAccountKeyInstallation {
  keyId: string;
  envelope: string;
  kek: Uint8Array;
}

export function isLocalStoreNewerError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'code' in error &&
    error.code === 'LOCAL_STORE_NEWER'
  );
}

/**
 * An IndexedDB open fails with VersionError when a newer app version already
 * upgraded the database; that becomes LOCAL_STORE_NEWER.
 */
export function localStoreOpenError(error: unknown): unknown {
  return error instanceof DOMException && error.name === 'VersionError'
    ? localAccountKeyError('LOCAL_STORE_NEWER')
    : error;
}

/** Wake signal for other tabs/workers holding keys of this account. */
export function localAccountLockChannel(accountId: string) {
  return `streamlain-local-account-lock:${accountId}`;
}

/** Held shared by every unlocked connection of the account; logout takes it
 * exclusively. */
export function localAccountAuthorityLock(accountId: string) {
  return `streamlain-local-account-authority:${accountId}`;
}

/** How long a logout waits for connections that cannot answer (a frozen tab). */
const LOGOUT_BARRIER_MS = 3_000;

/** Account state lives outside doc databases and survives their format reset. */
export class LocalAccountKeyStore {
  readonly accountId: string;
  readonly dbName: string;
  private readonly kekCustody: LocalAccountKekCustody;
  private database: Promise<IDBPDatabase<AccountKeySchema>> | null = null;
  private opened: IDBPDatabase<AccountKeySchema> | null = null;

  constructor(options: {
    accountId: string;
    kekCustody: LocalAccountKekCustody;
  }) {
    if (!options.accountId || !options.kekCustody)
      throw localAccountKeyError('DENIED');
    this.accountId = options.accountId;
    this.dbName = `streamlain-local-account:${options.accountId}`;
    this.kekCustody = options.kekCustody;
  }

  async read(): Promise<LocalAccountKeyRecord | null> {
    const db = await this.openDb();
    return (await db.get('account', this.accountId)) ?? null;
  }

  /** Caller must have verified the existing persistent App session first. */
  initialize(): Promise<LocalAccountKeyRecord> {
    return this.update(record => record ?? this.newRecord());
  }

  /** Only an explicitly completed normal login with fresh proof calls this. */
  async completeLogin(
    expectedAuthEpoch: number
  ): Promise<LocalAccountKeyRecord> {
    if (!Number.isSafeInteger(expectedAuthEpoch) || expectedAuthEpoch < 0)
      throw localAccountKeyError('LOCKED');
    const completed = await this.update(record => {
      const currentEpoch = record?.authEpoch ?? 0;
      if (
        currentEpoch !== expectedAuthEpoch ||
        currentEpoch >= Number.MAX_SAFE_INTEGER
      )
        throw localAccountKeyError('LOCKED');
      return {
        ...(record ?? this.newRecord()),
        authEpoch: currentEpoch + 1,
        logoutPending: false,
      };
    });
    await this.raiseMirrors(completed);
    return completed;
  }

  /**
   * Commits the logout edge before any sign-out request: one epoch step, the
   * KEK reference dropped (Web record or Desktop OS-vault entry), envelope/key
   * identity and ciphertext kept; the next normal login restores the KEK
   * through the server envelope open. Concurrent logouts share the edge;
   * absent metadata gets a locked record.
   */
  async beginLogout(): Promise<LocalAccountKeyRecord> {
    const locked = await this.update(record => {
      if (!record) return { ...this.newRecord(), logoutPending: true };
      if (record.logoutPending) return record;
      if (record.authEpoch >= Number.MAX_SAFE_INTEGER)
        throw localAccountKeyError('LOCKED');
      // Open connections lock at once, before the logout edge is written.
      this.announce(record.authEpoch + 1);
      return {
        ...record,
        authEpoch: record.authEpoch + 1,
        logoutPending: true,
        kek: null,
      };
    });
    await this.awaitConnectionsLocked();
    // A login that won the record after this edge owns its fresh vault entry.
    if (
      this.kekCustody.kind === 'os-vault' &&
      (await this.read())?.logoutPending
    )
      await this.kekCustody.vault.prune(this.accountId, null);
    await raiseAccountMirrors(this.accountId, {
      authEpoch: locked.authEpoch,
      logoutPending: locked.logoutPending,
    });
    return locked;
  }
  /**
   * Every IndexedDB database of the account refuses an older epoch from here
   * on (its authState mirror), and every open connection of an older epoch
   * hears it at once (the lock signal). Desktop raises its native databases
   * through LocalAccountKeys.
   */
  private async raiseMirrors(record: LocalAccountKeyRecord) {
    // The signal goes first: open connections lock even if a mirror fails.
    this.announce(record.authEpoch);
    await raiseAccountMirrors(this.accountId, {
      authEpoch: record.authEpoch,
      logoutPending: record.logoutPending,
    });
  }

  /**
   * Every unlocked connection of the account, in every tab and worker, holds
   * the authority lock shared until it locks, so taking it exclusively
   * returns once all have locked: no output of the old login follows the
   * logout. A connection that cannot answer, as in a frozen tab, holds the
   * logout up at most LOGOUT_BARRIER_MS; it locks on the signal or on its
   * check when it runs again.
   */
  private async awaitConnectionsLocked() {
    try {
      await navigator.locks.request(
        localAccountAuthorityLock(this.accountId),
        { mode: 'exclusive', signal: AbortSignal.timeout(LOGOUT_BARRIER_MS) },
        () => {}
      );
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'TimeoutError'))
        throw error;
      console.warn(
        '[LocalAccountKeyStore] logout went on without a connection that did not lock in time'
      );
    }
  }

  /** Every connection of an older epoch, in every realm, locks on this. */
  private announce(authEpoch: number) {
    const signal = new BroadcastChannel(
      localAccountLockChannel(this.accountId)
    );
    signal.postMessage({ authEpoch });
    signal.close();
  }

  /** Crypto/Vault work precedes the short IDB transaction; CAS is commit. */
  async installKeys(
    binding: LocalAuthBinding,
    input: LocalAccountKeyInstallation
  ): Promise<boolean> {
    try {
      this.assertAccount(binding);
      if (!input.keyId || !input.envelope || input.kek.byteLength !== 32)
        throw localAccountKeyError('DENIED');
      const initial = this.assertBinding(binding, await this.read());
      if (!this.isEmpty(initial)) return false;

      let kek: CryptoKey | null = null;
      if (this.kekCustody.kind === 'record') {
        kek = await importLocalDataKek(input.kek);
      } else {
        const status = await this.kekCustody.vault.write(
          this.accountId,
          input.keyId,
          initial.deviceId,
          input.kek
        );
        if (status !== 'ready') throw localAccountKeyError('KEY_UNAVAILABLE');
      }

      let installed = false;
      let selectedForCommit = false;
      try {
        await this.update(record => {
          const current = this.assertBinding(binding, record);
          if (!this.isEmpty(current)) return current;
          selectedForCommit = true;
          return {
            ...current,
            kek,
            envelope: input.envelope,
            keyId: input.keyId,
          };
        });
        installed = selectedForCommit;
      } finally {
        if (!installed && this.kekCustody.kind === 'os-vault') {
          const committed = await this.read();
          await this.kekCustody.vault.prune(
            this.accountId,
            committed?.keyId ?? null
          );
        }
      }
      return installed;
    } finally {
      input.kek.fill(0);
    }
  }

  /** Restore only the authenticated existing key identity, never allocate/replace it. */
  async restoreKek(
    binding: LocalAuthBinding,
    input: { keyId: string; kek: Uint8Array }
  ): Promise<void> {
    try {
      this.assertAccount(binding);
      const initial = this.assertBinding(binding, await this.read());
      if (!initial.envelope || initial.keyId !== input.keyId)
        throw localAccountKeyError('KEY_UNAVAILABLE');
      let kek: CryptoKey | null = null;
      if (this.kekCustody.kind === 'record') {
        kek = await importLocalDataKek(input.kek);
      } else {
        const status = await this.kekCustody.vault.write(
          this.accountId,
          input.keyId,
          initial.deviceId,
          input.kek
        );
        if (status !== 'ready') throw localAccountKeyError('KEY_UNAVAILABLE');
      }
      await this.update(record => {
        const current = this.assertBinding(binding, record);
        if (
          current.keyId !== input.keyId ||
          current.envelope !== initial.envelope
        )
          throw localAccountKeyError('KEY_UNAVAILABLE');
        return { ...current, kek: current.kek ?? kek };
      });
    } finally {
      input.kek.fill(0);
    }
  }

  /** Never provisions a replacement for absent key material. */
  async readKek(
    binding: LocalAuthBinding,
    operationRecord?: LocalAccountKeyRecord
  ): Promise<CryptoKey> {
    this.assertAccount(binding);
    // The provider passes its one operation-local authority read; the storage Tx
    // still checks the mirrored epoch immediately before returning/committing.
    const record = this.assertBinding(
      binding,
      operationRecord ?? (await this.read())
    );
    if (!record.envelope || !record.keyId)
      throw localAccountKeyError('KEY_UNAVAILABLE');

    if (this.kekCustody.kind === 'record') {
      if (!record.kek) throw localAccountKeyError('KEY_UNAVAILABLE');
      return record.kek;
    }

    const response = await this.kekCustody.vault.read(
      this.accountId,
      record.keyId
    );
    if (response.status !== 'ready' || !response.kek)
      throw localAccountKeyError('KEY_UNAVAILABLE');
    let kek: CryptoKey;
    try {
      kek = await importLocalDataKek(response.kek);
    } finally {
      response.kek.fill(0);
    }
    if (!operationRecord) {
      this.assertBinding(binding, await this.read());
      await this.kekCustody.vault.prune(this.accountId, record.keyId);
      this.assertBinding(binding, await this.read());
    }
    return kek;
  }

  private newRecord(): LocalAccountKeyRecord {
    return {
      accountId: this.accountId,
      deviceId: crypto.randomUUID(),
      authEpoch: 1,
      logoutPending: false,
      kek: null,
      envelope: null,
      keyId: null,
    };
  }

  private isEmpty(record: LocalAccountKeyRecord): boolean {
    return (
      record.kek === null && record.envelope === null && record.keyId === null
    );
  }

  private assertAccount(binding: LocalAuthBinding): void {
    if (binding.accountId !== this.accountId)
      throw localAccountKeyError('DENIED');
  }

  private assertBinding(
    binding: LocalAuthBinding,
    record: LocalAccountKeyRecord | null
  ): LocalAccountKeyRecord {
    if (
      !record ||
      record.accountId !== binding.accountId ||
      record.logoutPending ||
      !Number.isSafeInteger(binding.authEpoch) ||
      record.authEpoch !== binding.authEpoch
    )
      throw localAccountKeyError('LOCKED');
    return record;
  }

  close() {
    const pending = this.database;
    this.database = null;
    this.opened?.close();
    this.opened = null;
    void pending?.then(db => db.close()).catch(() => {});
  }

  private openDb(): Promise<IDBPDatabase<AccountKeySchema>> {
    if (this.database) return this.database;
    const pending = openDB<AccountKeySchema>(this.dbName, 1, {
      upgrade(db) {
        db.createObjectStore('account');
      },
      blocking: () => this.close(),
      terminated: () => {
        this.database = null;
        this.opened = null;
      },
    }).catch(error => {
      throw localStoreOpenError(error);
    });
    this.database = pending;
    void pending
      .then(db => {
        if (this.database !== pending) {
          db.close();
          return;
        }
        this.opened = db;
        db.addEventListener('close', () => {
          if (this.opened === db) {
            this.database = null;
            this.opened = null;
          }
        });
      })
      .catch(() => {
        if (this.database === pending) {
          this.database = null;
          this.opened = null;
        }
      });
    return pending;
  }

  private async update(
    change: (record: LocalAccountKeyRecord | null) => LocalAccountKeyRecord
  ): Promise<LocalAccountKeyRecord> {
    const db = await this.openDb();
    const tx = db.transaction('account', 'readwrite');
    try {
      const record = change((await tx.store.get(this.accountId)) ?? null);
      await tx.store.put(record, this.accountId);
      await tx.done;
      return record;
    } catch (error) {
      // Consume tx.done's rejection even for validation before the first write.
      try {
        tx.abort();
      } catch {
        // An already aborted transaction has nothing left to roll back.
      }
      await tx.done.catch(() => {});
      throw error;
    }
  }
}
